import axios from 'axios';
import { url } from '../../../env';
import { fuentesSinFaltasOpcionales, indiceCampos } from './columnasPm';
import { esAviso } from './columnas';

/*
 * Cliente HTTP del reporte mensual a Buró de Crédito (PF y PM/PFAE). Todo el acceso a
 * datos del módulo pasa por aquí: si el backend cambia la forma de una
 * respuesta, solo se toca este archivo. Contrato real: BuroReporteController.
 *
 * Rutas (grupo admin, prefijo `buro-reportes`; las respuestas NO van en `data`):
 *   GET    /                         [reporte]
 *   POST   /                         {tipo:'pf'|'pm_pfae', periodo:'YYYY-MM-DD' (último día del mes)} → detalle
 *   GET    /{reporte}                detalle
 *   PATCH  /{reporte}/filas/{fila}   {columna, valor, aplicar_en_sistema, motivo} → {reporte, filas}
 *   PATCH  /{reporte}/filas/{fila}/incluir   {incluir, motivo} → {reporte, fila}
 *   PATCH  /{reporte}/filas/{fila}/resultado-recuperacion   {forma?:'entrega'|'juicio',
 *                                    resultado?:'perdona'|'cobra'|'igual', monto_pendiente?} → {reporte, filas}
 *                                    Solo filas de auto recuperado (fila.recuperacion != null) de un reporte
 *                                    en borrador. Fila I = titular, C = aval ('igual' solo vale para el aval).
 *                                    'cobra' exige monto_pendiente > 0. Se guarda en el contrato (Cartera) y
 *                                    se rearman las filas del contrato (devuelve todas). Errores: 422 {message}.
 *   PATCH  /{reporte}/ajustes        {incluir_mora_en_saldo} → detalle
 *   POST   /{reporte}/regenerar | /cerrar | /enviado → detalle
 *   GET    /{reporte}/csv            archivo del formato (blob): CSV en PF, TXT en PM/PFAE (buro_pm_pfae_AAAAMM.txt).
 *                                    El nombre llega en Content-Disposition; si el navegador no lo ve se usa el de respaldo.
 *   POST   /{reporte}/csv-protegido  {password?} → 200 {nombre:'buro_pf_AAAAMM.zip', password, zip_base64}
 *                                    (PF cifra con AES-128; PM/PFAE con AES-256)
 *                                    Solo cerrado/enviado (borrador = 422). Sin password el servidor genera
 *                                    una; si se manda debe tener ≥8 caracteres, 1 mayúscula, 1 número y
 *                                    1 símbolo (si no, 422 {message}). La contraseña NO se guarda ni se
 *                                    vuelve a pedir: la pantalla la muestra una sola vez.
 *   GET    /codigo-postal/{cp}       {cp, encontrado, fuente:'sepomex'|'historial'|null, estado (clave
 *                                    de Buró), municipio, ciudad, colonias:[...]}
 *
 * Formas:
 *   reporte: id, tipo ('pf'|'pm_pfae'), periodo (corte, 'YYYY-MM-DD'), estatus ('borrador'|
 *     'cerrado'|'enviado'), ajustes {incluir_mora_en_saldo} ({} en PM), totales
 *     {saldo_actual, saldo_vencido}, filas_total, filas_con_error, cerrado_en,
 *     enviado_en, campos (esquema PM de SegmentosPm::paraFront(); [] en PF),
 *     pendientes_cierre (textos sin datos personales de lo externo que aún bloquea cerrar;
 *     [] si el reporte no es borrador). El detalle suma filas[] y columnas_corregibles.
 *   fila: id, credito_id, numero_cuenta, responsabilidad, incluir,
 *     motivo_exclusion, valores {'1'..'71'}, sugeridos {col: valor armado por el
 *     sistema; es el "valor del sistema"}, fuentes {col: 'sistema'|'sugerencia'|
 *     'falta'|'manual'|'fijo'}, errores [{columna, regla, mensaje}], notas {col: texto}.
 *   columna 0 = tipo de persona (PF|PFAE|PM): siempre se guarda en el sistema y
 *     si no es PF la fila queda excluida. Un error con columna 0 = falta el tipo.
 *   detalle.columnas_corregibles: [n] columnas que admiten "corregir también en el sistema" (0 = tipo de persona).
 *   fila.cliente_ligado (false = no se puede guardar en el sistema, solo en el reporte).
 *   fila.tipo_persona ('PF'|'PFAE'|'PM', el resuelto) y fila.tipo_persona_estado {tipo, confirmado, archivo, motivo}.
 *   PM: las claves de valores/fuentes/errores/notas son códigos de 4 dígitos (segmento·1000 + lista·100 + etiqueta,
 *     p. ej. EM03 = 1003, AC[2].08 = 2208, CR19 = 3019); fuentes trae 'falta' en TODA celda editable vacía,
 *     también las opcionales (ver fuentesSinFaltasOpcionales).
 *   fila.cliente (nombre armado) y fila.company_id; responsabilidad 'C' = aval.
 *   fila.recuperacion: null, o {persona:'titular'|'aval', forma:'entrega'|'juicio', resultado (efectivo:
 *     'cubre'|'perdona'|'cobra'), resultado_guardado ('cubre'|'perdona'|'cobra'|'igual'|null si no hay
 *     nada guardado), faltante, monto_pendiente, pagado, fecha_pago ('YYYY-MM-DD'|null), valor_auto}.
 *     No es null en las filas de contratos cerrados por recuperación de auto.
 *   PATCH de fila también acepta {cambios:[{columna, valor}], aplicar_en_sistema, motivo} (varias columnas, una transacción).
 *   PATCH de celda: con aplicar_en_sistema vienen todas las filas del cliente.
 *   Errores de validación: 422 con { message } en español, se pintan tal cual.
 *
 * LO QUE EL BACKEND NO MANDA Y AQUÍ SE DEDUCE (ver el reporte de trabajo):
 *   - Agrupación de contratos del cliente: company_id, o credito_id si es null.
 *   - Contadores (listos, con error, con sugerencia, excluidos, contratos): se
 *     calculan de las filas.
 *   - Historial de cambios: no hay endpoint; la pantalla oculta el botón.
 */

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  Accept: 'application/json',
});

const jsonHeaders = () => ({
  ...authHeaders(),
  'Content-Type': 'application/json',
});

const base = `${url}/buro-reportes`;

const num = (v) => Number(v) || 0;

// Un aviso de confirmación con Buró (pendiente_buro_vr/fr) no es un dato mal
// capturado: no cuenta como "fila con error" (va como sugerencia/amarillo).
const erroresReales = (f) => (f.errores || []).filter((e) => !esAviso(e));
const filaConError = (f) => f.incluir && (erroresReales(f).length > 0 || Object.values(f.fuentes).includes('falta'));
const filaConSugerencia = (f) => f.incluir && (Object.values(f.fuentes).includes('sugerencia') || (f.errores || []).some(esAviso));

// Respaldo si el backend no manda `cliente`.
const nombreDe = (f) => {
  const v = f.valores;
  if (v['1003']) return v['1003'];
  if (v['1004']) return [v['1004'], v['1005'], v['1006'], v['1007']].filter(Boolean).join(' ');
  const partes = [v['7'], v['8'], v['4'], v['5']].filter((x) => x && x !== 'NO PROPORCIONADO');
  if (partes.length) return partes.join(' ');
  const libre = Object.values(f.notas).find((n) => String(n).startsWith('nombre sin separar: '));
  return libre ? String(libre).slice('nombre sin separar: '.length) : f.numero_cuenta;
};

const normalizarFila = (f, indice) => {
  // El backend manda el tipo resuelto como texto y su estado aparte; antes iba todo en un objeto.
  const estadoTipo = f.tipo_persona_estado || (typeof f.tipo_persona === 'object' ? f.tipo_persona : null);
  const fila = {
    ...f,
    tipo_persona: typeof f.tipo_persona === 'object' && f.tipo_persona !== null ? f.tipo_persona.tipo : f.tipo_persona,
    tipo_confirmado: estadoTipo ? estadoTipo.confirmado !== false : true,
    valores: f.valores || {},
    sugeridos: f.sugeridos || {},
    fuentes: fuentesSinFaltasOpcionales(f.valores, f.fuentes || {}, indice),
    errores: f.errores || [],
    notas: f.notas || {},
  };
  fila.cliente = f.cliente || nombreDe(fila);
  fila.aval = f.responsabilidad === 'C';
  // Sin presupuesto/usuario: no hay cliente al que corregirle datos en el sistema.
  fila.cliente_ligado = f.cliente_ligado !== false;
  // Clave de agrupación de los contratos de un mismo cliente. Un aval es otra
  // persona: nunca se junta con el titular.
  fila.clienteKey = fila.aval ? `aval-${f.id}` : (f.company_id != null ? `company-${f.company_id}` : `credito-${f.credito_id}`);
  return fila;
};

// Completa lo que el backend no manda: filas normalizadas y contadores.
export const normalizarReporte = (r) => {
  if (!r) return r;
  // El índice del esquema solo se arma si hay filas que normalizar (la lista de reportes trae `campos` sin filas).
  const indice = r.filas ? indiceCampos(r.campos) : null;
  const filas = (r.filas || []).map((f) => normalizarFila(f, indice));
  const incl = filas.filter((f) => f.incluir);
  const t = { ...(r.totales || {}) };
  if (r.filas) {
    t.contratos = incl.length;
    t.excluidos = filas.length - incl.length;
    t.con_error = incl.filter(filaConError).length;
    t.con_sugerencia = incl.filter((f) => !filaConError(f) && filaConSugerencia(f)).length;
    // Filas cuyo único problema es un aviso de confirmación con Buró (no un dato
    // faltante): no son "con_error", pero el backend aún bloquea el cierre.
    t.con_aviso = incl.filter((f) => !filaConError(f) && (f.errores || []).some(esAviso)).length;
    t.listos = t.contratos - t.con_error - t.con_sugerencia;
  } else {
    t.contratos = r.filas_total;
    t.con_error = r.filas_con_error;
  }
  t.saldo_actual = num(t.saldo_actual);
  t.saldo_vencido = num(t.saldo_vencido);
  return {
    ...r,
    columnas_corregibles: r.columnas_corregibles,
    pendientes_cierre: r.pendientes_cierre || [],
    filas: r.filas ? filas : undefined,
    ajustes: { incluir_mora_en_saldo: false, ...(r.ajustes || {}) },
    totales: t,
  };
};

// Mezcla la respuesta de un PATCH de fila ({reporte, filas|fila}) en el detalle ya cargado.
export const fusionarFilas = (previo, resp) => {
  const nuevas = resp.filas || (resp.fila ? [resp.fila] : []);
  const porId = new Map(nuevas.map((f) => [f.id, f]));
  return normalizarReporte({
    ...previo,
    ...resp.reporte,
    filas: previo.filas.map((f) => porId.get(f.id) || f),
  });
};

const leerTexto = (blob) => new Promise((resolve, reject) => {
  const lector = new FileReader();
  lector.onload = () => resolve(String(lector.result));
  lector.onerror = () => reject(lector.error);
  lector.readAsText(blob);
});

export const buroApi = {
  getReportes: async (signal) => {
    const res = await axios.get(base, { headers: authHeaders(), signal });
    return (Array.isArray(res.data) ? res.data : []).map(normalizarReporte);
  },

  getReporte: async (id, signal) => {
    const res = await axios.get(`${base}/${id}`, { headers: authHeaders(), signal });
    return normalizarReporte(res.data);
  },

  // `corte` = último día del mes, 'YYYY-MM-DD'.
  crearReporte: async (tipo, corte) => {
    // Compatibilidad con llamadas PF anteriores.
    if (corte === undefined) { corte = tipo; tipo = 'pf'; }
    const res = await axios.post(base, { tipo, periodo: corte }, { headers: jsonHeaders() });
    return normalizarReporte(res.data);
  },

  // `columna`: número de columna de Buró (1..71) o 0 = tipo de persona.
  // Devuelve { reporte, filas } crudo; usar fusionarFilas.
  // `confirmar: true` acepta la sugerencia tal cual (sin valor): la celda pasa a sistema.
  editarCelda: async (id, filaId, { columna, valor, aplicarEnSistema = false, motivo = '', confirmar = false }) => {
    const body = confirmar
      ? { columna: Number(columna), confirmar: true, motivo: motivo || null }
      : { columna: Number(columna), valor: valor ?? '', aplicar_en_sistema: aplicarEnSistema, motivo: motivo || null };
    const res = await axios.patch(`${base}/${id}/filas/${filaId}`, body, { headers: jsonHeaders() });
    return res.data;
  },

  // Varias columnas (1..71) de una fila en UNA petición y una transacción:
  // `cambios` = [{columna, valor}]. Mismo formato de respuesta que editarCelda.
  // El tipo de persona (columna 0) va aparte, con editarCelda.
  editarCeldas: async (id, filaId, { cambios, aplicarEnSistema = false, motivo = '' }) => {
    const body = {
      cambios: cambios.map((c) => ({ columna: Number(c.columna), valor: c.valor ?? '' })),
      aplicar_en_sistema: aplicarEnSistema,
      motivo: motivo || null,
    };
    const res = await axios.patch(`${base}/${id}/filas/${filaId}`, body, { headers: jsonHeaders() });
    return res.data;
  },

  // Devuelve { reporte, fila } crudo; usar fusionarFilas.
  incluirFila: async (id, filaId, { incluir, motivo = '' }) => {
    const res = await axios.patch(
      `${base}/${id}/filas/${filaId}/incluir`,
      { incluir, motivo: motivo || null },
      { headers: jsonHeaders() },
    );
    return res.data;
  },

  // Resultado para Buró de un auto recuperado, desde la fila de ese contrato.
  // `body` = { forma?, resultado?, monto_pendiente? } tal cual (ver arriba).
  // Devuelve { reporte, filas } crudo (todas las filas del contrato); usar fusionarFilas.
  resultadoRecuperacion: async (id, filaId, body) => {
    const res = await axios.patch(`${base}/${id}/filas/${filaId}/resultado-recuperacion`, body, { headers: jsonHeaders() });
    return res.data;
  },

  // Las cuatro devuelven el detalle completo del reporte.
  guardarAjustes: async (id, ajustes) => {
    const res = await axios.patch(`${base}/${id}/ajustes`, ajustes, { headers: jsonHeaders() });
    return normalizarReporte(res.data);
  },
  regenerar: async (id) => normalizarReporte((await axios.post(`${base}/${id}/regenerar`, {}, { headers: jsonHeaders() })).data),
  cerrar: async (id) => normalizarReporte((await axios.post(`${base}/${id}/cerrar`, {}, { headers: jsonHeaders() })).data),
  marcarEnviado: async (id) => normalizarReporte((await axios.post(`${base}/${id}/enviado`, {}, { headers: jsonHeaders() })).data),

  // Catálogo por C.P. (5 dígitos): estado, municipio, ciudad y colonias.
  getCodigoPostal: async (cp, signal) => {
    const res = await axios.get(`${base}/codigo-postal/${cp}`, { headers: authHeaders(), signal });
    return res.data;
  },

  // Config PM editable (HD00/02/07 + confirmaciones). Lo que falta para cerrar.
  getConfigPm: async (signal) => {
    const res = await axios.get(`${base}/config-pm`, { headers: authHeaders(), signal });
    return res.data;
  },
  guardarConfigPm: async (datos) => {
    const res = await axios.put(`${base}/config-pm`, datos, { headers: jsonHeaders() });
    return res.data;
  },
  // Confirma (o retira) un aviso de "confirmar con Buró" (VR/FR) de una fila.
  // Mismo formato de respuesta que editarCelda (la tabla usa fusionarFilas).
  confirmarAviso: async (id, filaId, { regla, confirmar = true, motivo = '' }) => {
    const res = await axios.patch(`${base}/${id}/filas/${filaId}/confirmar-aviso`, { regla, confirmar, motivo: motivo || null }, { headers: jsonHeaders() });
    return res.data;
  },

  // ZIP protegido con contraseña. Devuelve { blob, nombre, password }. Con
  // `password` vacío el servidor genera una. La descarga queda registrada en el servidor.
  descargarCsvProtegido: async (id, password = '') => {
    const res = await axios.post(
      `${base}/${id}/csv-protegido`,
      password ? { password } : {},
      { headers: jsonHeaders() },
    );
    const { nombre, password: clave, zip_base64: b64 } = res.data;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return { blob: new Blob([bytes], { type: 'application/zip' }), nombre, password: clave };
  },

  // Devuelve { blob, nombre }. El nombre sale del Content-Disposition si viene.
  // Un 422 llega como blob: se lee su {message} para que mensajeError lo muestre.
  descargarCsv: async (id, respaldo = 'buro_pf.csv') => {
    let res;
    try {
      res = await axios.get(`${base}/${id}/csv`, { headers: authHeaders(), responseType: 'blob' });
    } catch (e) {
      const data = e?.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try { e.response.data = JSON.parse(await leerTexto(data)); } catch (_) { /* no era JSON */ }
      }
      throw e;
    }
    const cd = res.headers?.['content-disposition'] || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return { blob: res.data, nombre: m ? decodeURIComponent(m[1]) : respaldo };
  },

  // CSV LEGIBLE de revisión (solo PM/PFAE). No es el archivo que se envía a Buró.
  descargarCsvRevision: async (id, respaldo = 'buro_pm_pfae_revision.csv') => {
    let res;
    try {
      res = await axios.get(`${base}/${id}/csv-revision`, { headers: authHeaders(), responseType: 'blob' });
    } catch (e) {
      const data = e?.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try { e.response.data = JSON.parse(await leerTexto(data)); } catch (_) { /* no era JSON */ }
      }
      throw e;
    }
    const cd = res.headers?.['content-disposition'] || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return { blob: res.data, nombre: m ? decodeURIComponent(m[1]) : respaldo };
  },
};

// Rutas históricas conservadas; el backend decide CSV/TXT y cifrado por tipo.
buroApi.descargarArchivo = (...args) => buroApi.descargarCsv(...args);
buroApi.descargarArchivoProtegido = (...args) => buroApi.descargarCsvProtegido(...args);

export default buroApi;
